import { HttpClientModule } from '@angular/common/http';
import { Injector, NgModule, Optional } from '@angular/core';
import { TransferState, makeStateKey } from '@angular/platform-browser';

import { ApolloCache } from '@apollo/client/cache';
import { ApolloClientOptions, ApolloLink, NormalizedCacheObject } from '@apollo/client/core';
import { APOLLO_OPTIONS, Apollo, ApolloModule } from 'apollo-angular';
import { HttpLink, HttpLinkHandler } from 'apollo-angular/http';

import { EnvironmentService, FeatureToggleService, PlatformService, TokenService } from '@hdms/shared/util-core';

import { CustomInMemoryCache } from './cache/custom-in-memory.cache';
import { IN_MEMORY_CACHE_CONFIG } from './cache/in-memory-cache.config';
import { AuthLink } from './links/auth.link';
import { createErrorLink } from './links/error.link';
import { createHeadersLink } from './links/headers.link';
import { createReadyLink } from './links/ready.link';

const STATE_KEY = makeStateKey<NormalizedCacheObject>('apollo.state');
const APOLLO_CLIENT_NAME = 'hdms-frontend';
const APOLLO_DEFAULT_VERSION = 'default';

/**
 * Creates the client options to be used for the client.
 * @param endpointLink The link to be used in the client.
 * @param injector
 * @returns The created client options.
 */
function createClientOptions(
  endpointLink: HttpLinkHandler,
  injector: Injector
): ApolloClientOptions<NormalizedCacheObject> {
  const platform = injector.get(PlatformService);
  const envService = injector.get(EnvironmentService);
  const tokenService = injector.get(TokenService);
  const ftService = injector.get(FeatureToggleService);

  const links = [createErrorLink(injector), createHeadersLink(tokenService, ftService), endpointLink];

  // insert ReadyLink + AuthLink after error link as for processing the response the order is right to left
  envService.isStandalone && links.splice(1, 0, createReadyLink(tokenService), new AuthLink(injector));

  // in browser queries with `forceFetch` enabled will be delayed
  // on server avoid to run twice queries with `forceFetch` enabled
  const ssrOptions = platform.isBrowser ? { ssrForceFetchDelay: 100 } : { ssrMode: true };

  const version = envService.isProduction ? envService.releaseVersion : APOLLO_DEFAULT_VERSION;

  return {
    cache: new CustomInMemoryCache(IN_MEMORY_CACHE_CONFIG),
    link: ApolloLink.from(links),
    ...ssrOptions,
    // name and version are required for Apollo Studio
    name: APOLLO_CLIENT_NAME,
    version
  };
}

/**
 * Creates the options for the standard Apollo client.
 * @param httpLink
 * @param injector
 * @returns The created options for the standard client.
 */
function createApollo(httpLink: HttpLink, injector: Injector): ApolloClientOptions<NormalizedCacheObject> {
  const envService = injector.get(EnvironmentService);

  const link = httpLink.create({
    uri: envService.gqlEndpointUri,
    withCredentials: envService.isStandalone
  });

  return createClientOptions(link, injector);
}

@NgModule({
  imports: [HttpClientModule, ApolloModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, Injector]
    }
  ]
})
export class GraphQLModule {
  private readonly cache: ApolloCache<NormalizedCacheObject>;

  constructor(
    apollo: Apollo,
    platformService: PlatformService,
    @Optional() private readonly transferState: TransferState
  ) {
    if (!transferState) {
      return;
    }

    this.cache = apollo.client.cache;

    if (platformService.isBrowser) {
      this.reHydrateCache();
    } else {
      this.extractCache();
    }
  }

  private extractCache(): void {
    this.transferState.onSerialize(STATE_KEY, () => this.cache.extract());
  }

  private reHydrateCache(): void {
    const state = this.transferState.get<NormalizedCacheObject>(STATE_KEY, null);
    if (state) {
      this.cache.restore(state);
    }
  }
}
